/**
 * Manifesto e endereços das varreduras publicadas pelo espelho do
 * `AmaView-dados`.
 *
 * Por que um espelho, e não o SIPAM direto: o `siger.sipam.gov.br` não manda
 * cabeçalho CORS. O navegador não lê a listagem, e um PNG carregado sem CORS
 * "suja" o canvas do mapa — a exportação de vídeo quebraria com o radar
 * ligado. O workflow do `AmaView-dados` republica as últimas 48 h no GitHub
 * Pages (CORS `*`) a cada ciclo de 15 min, com os bytes idênticos aos do SIPAM.
 */
import { fetchWithRetry } from '../lib/fetchRetry';
import { isProduto, PRODUTOS, type Produto } from './produtos';

/** Onde o espelho publica. `VITE_RADAR_URL` aponta para outro lugar em desenvolvimento. */
export const BASE_RADAR = (import.meta.env.VITE_RADAR_URL || 'https://helvecioneto.github.io/AmaView-dados/radar').replace(
  /\/$/,
  '',
);

export const SIPAM_URL = 'https://www.gov.br/censipam/pt-br';

export const SIPAM_ATRIBUICAO = `Radares: <a href="${SIPAM_URL}" target="_blank" rel="noopener">SIPAM/Censipam</a>`;

/** Tamanho padrão do PNG (todos os radares menos Belém, que publica 1000 px). */
export const PX_PADRAO: [number, number] = [954, 954];

export interface ProdutoRadar {
  /** Instantes das varreduras publicadas (epoch ms, UTC), crescente. */
  t: number[];
  /** A listagem do SIPAM falhou no último ciclo e a lista veio do anterior. */
  herdado: boolean;
  erro: string | null;
}

export interface RadarInfo {
  id: string;
  nome: string;
  /** Bordas externas do PNG em lon/lat: [minx, miny, maxx, maxy]. */
  extent: [number, number, number, number];
  /** Tamanho do PNG mais recente. */
  px: [number, number];
  produtos: Record<Produto, ProdutoRadar>;
}

export interface Manifesto {
  /** Quando o espelho publicou (epoch ms), ou null. */
  gerado: number | null;
  janelaHoras: number;
  radares: RadarInfo[];
}

const pad = (n: number, len = 2) => String(n).padStart(len, '0');

/** epoch ms → "AAAA_MM_DD_HH_MM_SS" (UTC), o nome do arquivo no SIPAM. */
export function carimbo(ms: number): string {
  const d = new Date(ms);
  return (
    `${d.getUTCFullYear()}_${pad(d.getUTCMonth() + 1)}_${pad(d.getUTCDate())}_` +
    `${pad(d.getUTCHours())}_${pad(d.getUTCMinutes())}_${pad(d.getUTCSeconds())}`
  );
}

export function urlVarredura(radar: string, produto: Produto, ms: number, base: string = BASE_RADAR): string {
  return `${base}/${radar}/${produto}/${carimbo(ms)}.png`;
}

/** Chave do quadro no cache de imagens: radar + produto. */
export function chaveCache(radar: string, produto: Produto): string {
  return `${radar}:${produto}`;
}

export function radarDaChave(chave: string): string {
  return chave.split(':')[0];
}

function extentValido(x: unknown): x is [number, number, number, number] {
  if (!Array.isArray(x) || x.length !== 4 || !x.every((n) => typeof n === 'number' && Number.isFinite(n))) return false;
  const [a, b, c, d] = x as number[];
  const w = c - a;
  const h = d - b;
  // Um radar cobre ~4,35°: fora de 1–10° é dado quebrado, não outro radar.
  return w > 1 && w < 10 && h > 1 && h < 10 && a >= -180 && c <= 180 && b >= -90 && d <= 90;
}

function pxValido(x: unknown): x is [number, number] {
  return Array.isArray(x) && x.length === 2 && x.every((n) => Number.isInteger(n) && n > 0 && n <= 8192);
}

/**
 * Interpreta o manifesto. Radar com extensão inválida é descartado (sem
 * georreferência não há onde desenhar); instantes inválidos ou fora de ordem
 * são limpos. Lança erro só quando o arquivo inteiro não é um manifesto.
 */
export function lerManifesto(cru: unknown): Manifesto {
  const m = cru as { gerado?: unknown; janelaHoras?: unknown; radares?: unknown };
  if (!m || typeof m !== 'object' || !Array.isArray(m.radares)) throw new Error('manifesto do radar sem a lista `radares`');
  const radares: RadarInfo[] = [];
  for (const r of m.radares as Record<string, unknown>[]) {
    if (!r || typeof r.id !== 'string' || !/^[a-z0-9]{2,8}$/.test(r.id) || !extentValido(r.extent)) continue;
    const produtosCru = (r.produtos ?? {}) as Record<string, { t?: unknown; herdado?: unknown; erro?: unknown }>;
    const produtos = {} as Record<Produto, ProdutoRadar>;
    for (const p of PRODUTOS) {
      const cp = produtosCru[p];
      const seg = Array.isArray(cp?.t) ? cp.t : [];
      const t = [...new Set(seg.filter((n): n is number => typeof n === 'number' && Number.isFinite(n) && n > 0))]
        .map((s) => s * 1000)
        .sort((a, b) => a - b);
      produtos[p] = {
        t,
        herdado: cp?.herdado === true,
        erro: typeof cp?.erro === 'string' && cp.erro ? cp.erro : null,
      };
    }
    radares.push({
      id: r.id,
      nome: typeof r.nome === 'string' && r.nome.trim() ? r.nome.trim() : r.id.toUpperCase(),
      extent: r.extent,
      px: pxValido(r.px) ? r.px : PX_PADRAO,
      produtos,
    });
  }
  radares.sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'));
  const gerado = typeof m.gerado === 'string' ? Date.parse(m.gerado) : NaN;
  return {
    gerado: Number.isFinite(gerado) ? gerado : null,
    janelaHoras: typeof m.janelaHoras === 'number' && m.janelaHoras > 0 ? m.janelaHoras : 48,
    radares,
  };
}

/**
 * Baixa o manifesto. O `?m=` muda a cada minuto e fura a CDN do Pages
 * (`max-age=600`): sem isso, a varredura mais nova podia chegar 10 min
 * atrasada além do atraso do próprio radar. Um pedido por minuto por
 * visitante, de ~15 KB comprimido.
 */
export async function carregarManifesto(signal?: AbortSignal): Promise<Manifesto> {
  const m = Math.floor(Date.now() / 60_000);
  const cru = await fetchWithRetry<unknown>(`${BASE_RADAR}/manifest.json?m=${m}`, { cache: 'no-cache' }, {
    signal,
    read: (r) => r.json() as Promise<unknown>,
  });
  return lerManifesto(cru);
}

export { isProduto };
